import { useEffect, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import { Image } from 'expo-image';
import { Ionicons } from '@expo/vector-icons';
import { api } from '~/api';
import { formatCurrency } from '~/lib/currency';

interface PayUModalProps {
    visible: boolean;
    onClose: (state: 'successful' | 'failed' | 'pending') => void;
    onViewBooking: () => void;
    onOpenPayment: () => void;
    onPaymentSuccess?: () => void;
    amount: number;
    currency?: string;
    inrAmount?: number;
    transactionId?: string;
    partnerOrderId?: string;
    hasPaymentPage: boolean;
    popupBlocked: boolean;
    openingPayment: boolean;
    redirectSeconds: number | null;
    initiationError: string | null;
}

export function PayUModal({ visible, onClose, onViewBooking, onOpenPayment, onPaymentSuccess, amount, currency = 'INR', transactionId, partnerOrderId, hasPaymentPage, popupBlocked, openingPayment, redirectSeconds, initiationError }: PayUModalProps) {
    const [paymentStatus, setPaymentStatus] = useState<'verifying' | 'successful' | 'failed'>('verifying');
    const [statusError, setStatusError] = useState<string | null>(null);

    useEffect(() => {
        setPaymentStatus('verifying');
        setStatusError(null);
    }, [transactionId]);

    // The server is authoritative; a delayed PayU callback is not a failed payment.
    useEffect(() => {
        if (!visible || !transactionId || !partnerOrderId || paymentStatus !== 'verifying') return;
        let active = true;
        let timer: ReturnType<typeof setTimeout>;
        const check = async () => {
            try {
                const result = await api.payment.status({ transactionId, partnerOrderId });
                if (!active) return;
                if (result.data?.paymentStatus === 'successful') setPaymentStatus('successful');
                else if (result.data?.paymentStatus === 'failed') {
                    setPaymentStatus('failed');
                    setStatusError(result.data.error || 'The payment was not completed.');
                }
            } catch { /* Keep checking after transient errors. */ }
            if (active) timer = setTimeout(check, 1000);
        };
        void check();
        return () => { active = false; clearTimeout(timer); };
    }, [visible, transactionId, partnerOrderId, paymentStatus]);

    // Once PayU confirms, close its window and move on without waiting for a click.
    useEffect(() => {
        if (!visible || paymentStatus !== 'successful') return;
        onPaymentSuccess?.();
        const timer = setTimeout(onViewBooking, 800);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, paymentStatus]);

    if (!visible) return null;
    const success = paymentStatus === 'successful';
    const failed = paymentStatus === 'failed' || Boolean(initiationError);
    const closeState = success ? 'successful' : failed ? 'failed' : 'pending';
    const preparing = openingPayment && !hasPaymentPage && !transactionId;
    const title = success ? 'Payment received' : failed ? 'Payment needs attention'
        : preparing ? 'Getting your reservation ready' : hasPaymentPage && redirectSeconds !== null
            ? 'Reviewing your payment details' : 'Complete your payment with PayU';
    const description = success
        ? 'We’re confirming your reservation with the hotel. You can view the latest status below.'
        : initiationError || statusError || (failed ? 'Your payment could not be completed.'
            : preparing ? 'We’re preparing your secure checkout.'
                : hasPaymentPage ? 'Your secure payment page is ready.'
                    : 'We’re checking your payment status.');

    return <Modal visible={visible} transparent animationType="fade" onRequestClose={() => onClose(closeState)}>
        <View style={styles.backdrop}>
            <View style={styles.card}>
                <Pressable accessibilityRole="button" accessibilityLabel="Close payment status" onPress={() => onClose(closeState)} style={styles.close}><Text style={styles.closeText}>×</Text></Pressable>
                <Text style={styles.eyebrow}>Secure checkout</Text>
                <View style={[styles.icon, success && styles.successIcon, failed && styles.failedIcon]}>
                    {success || failed ? <Text style={styles.iconText}>{success ? '✓' : '!'}</Text> : <Ionicons name="card-outline" size={25} color="#2345B5" />}
                </View>
                <Text style={styles.title}>{title}</Text>
                <Text style={styles.description}>{description}</Text>
                <View style={styles.amountBox}>
                    <Text style={styles.amountLabel}>Payment total</Text>
                    <Text style={styles.amount}>{formatCurrency(amount, currency)}</Text>
                </View>
                {!success && !failed && popupBlocked ? <Text style={styles.countdown}>The payment page didn’t open. Continue below.</Text> : null}
                {success ? <Pressable accessibilityRole="button" onPress={onViewBooking} style={styles.button}><Text style={styles.buttonText}>View booking</Text></Pressable>
                    : !failed && (hasPaymentPage || openingPayment) ? <Pressable accessibilityRole="button" accessibilityState={{ disabled: !hasPaymentPage }} disabled={!hasPaymentPage} onPress={onOpenPayment} style={[styles.button, !hasPaymentPage && styles.buttonDisabled]}><Text style={styles.buttonText}>Continue to payment</Text></Pressable>
                        : failed ? <Pressable accessibilityRole="button" onPress={() => onClose(closeState)} style={styles.button}><Text style={styles.buttonText}>{initiationError ? 'Return to checkout' : 'View payment status'}</Text></Pressable> : null}
                <View style={styles.partner}><Text style={styles.partnerLabel}>Payment handled by</Text><Image source={require('../../assets/images/payu_logo.svg')} contentFit="contain" style={styles.partnerLogo} accessibilityLabel="PayU" /></View>
            </View>
        </View>
    </Modal>;
}

const styles = StyleSheet.create({
    backdrop: { flex: 1, backgroundColor: 'rgba(20, 30, 52, 0.32)', justifyContent: 'center', alignItems: 'center', padding: 16 },
    card: { width: '100%', maxWidth: 540, backgroundColor: '#fff', borderRadius: 24, padding: 28, alignItems: 'center', shadowColor: '#14213D', shadowOpacity: 0.12, shadowRadius: 28, elevation: 10 },
    close: { position: 'absolute', top: 14, right: 18, padding: 8, zIndex: 1 },
    closeText: { color: '#64748B', fontSize: 28, lineHeight: 30 },
    eyebrow: { color: '#64748B', fontSize: 12, fontWeight: '600', textAlign: 'center' },
    icon: { width: 52, height: 52, borderRadius: 26, backgroundColor: '#EEF2FF', justifyContent: 'center', alignItems: 'center', marginTop: 25, marginBottom: 18 },
    successIcon: { backgroundColor: '#059669' },
    failedIcon: { backgroundColor: '#D97706' },
    iconText: { color: '#fff', fontSize: 27, fontWeight: '700' },
    title: { color: '#14213D', fontSize: 24, fontWeight: '700', textAlign: 'center', maxWidth: 380 },
    description: { color: '#475569', fontSize: 14, lineHeight: 21, textAlign: 'center', marginTop: 10, maxWidth: 380 },
    amountBox: { alignSelf: 'stretch', backgroundColor: '#F6F8FC', borderRadius: 14, padding: 17, marginTop: 24, marginBottom: 18, alignItems: 'center' },
    amountLabel: { color: '#64748B', fontSize: 12, fontWeight: '600', marginBottom: 5 },
    amount: { color: '#14213D', fontSize: 25, fontWeight: '700' },
    countdown: { color: '#475569', fontSize: 13, fontWeight: '500', marginBottom: 14, textAlign: 'center' },
    button: { backgroundColor: '#2345B5', borderRadius: 10, minHeight: 48, paddingHorizontal: 22, justifyContent: 'center', alignItems: 'center', alignSelf: 'stretch' },
    buttonDisabled: { opacity: 0.5 },
    buttonText: { color: '#fff', fontSize: 15, fontWeight: '700' },
    partner: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16 },
    partnerLabel: { color: '#64748B', fontSize: 12 },
    partnerLogo: { width: 54, height: 27 },
});
